import { lazy, Suspense, useImperativeHandle, useRef, useState } from "react";

import {
  faArrowTurnDownLeft,
  faAt,
  faXmark,
  IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";
import type { MDXEditorMethods } from "@mdxeditor/editor";

import {
  useReferenceItems,
  useReferenceRendering,
} from "src/base-components/Chat/ChatReferences";
import {
  mentionKey,
  type PromptBoxMention,
  resolveKind,
} from "src/base-components/Chat/PromptBox/mentionContent";
import type { PromptBoxMentionsApi } from "src/base-components/Chat/PromptBox/promptBoxMentionsPlugin";
import { Button } from "src/design-system/Button";
import { IconButton } from "src/design-system/Icon";
import { Pill, PillVariants, resolvePillVariant } from "src/design-system/Pill";
import { Skeleton } from "src/design-system/Skeleton";

/**
 * Lazy-loaded component for the prompt box editor.
 * to avoid bundling the mdxeditor code into the main bundle.
 */
const PromptBoxEditor = lazy(() =>
  import("src/base-components/Chat/PromptBox/PromptBoxEditor").then((m) => ({
    default: m.PromptBoxEditor,
  })),
);

export type CustomMentionPill = {
  icon?: IconDefinition;
  label: string;
  key: string;
  isDeletable?: boolean;
  variant?: PillVariants;
};

const MentionPill: React.FC<
  Omit<CustomMentionPill, "key"> & { onDelete?: () => void }
> = ({ icon, label, isDeletable, variant = "gray", onDelete }) => {
  return (
    <Pill size="sm" variant={variant}>
      {icon && <Pill.Icon icon={icon} />}
      <Pill.Text>{label}</Pill.Text>
      {isDeletable && (
        <Pill.Icon
          icon={faXmark}
          onClick={(e) => {
            e.stopPropagation();
            onDelete?.();
          }}
        />
      )}
    </Pill>
  );
};

const dedupeMentions = (mentions: PromptBoxMention[]): PromptBoxMention[] => [
  ...new Map(mentions.map((m) => [mentionKey(m), m])).values(),
];

export type PromptBoxMentionInsert = {
  value: string;
  data?: Record<string, string>;
};

export type PromptBoxApi = {
  /**
   * Appends a mention at the end of the current content, leaving the cursor
   * right after it. Queued until the lazy editor mounts.
   */
  appendMention: (mention: PromptBoxMentionInsert) => void;
};

type Props = {
  ref?: React.Ref<PromptBoxApi>;
  initialValue?: string;
  disabled?: boolean;
  /** Disables only the submit button, keeping the editor typable (e.g. while
   * the assistant is responding). */
  submitDisabled?: boolean;
  onChange?: (value: string) => void;
  onSubmit: (content: string, mentions: PromptBoxMention[]) => void;
  placeholder?: string;
  submitLabel?: string;
  dataLoc?: string;
  customMentionPills?: CustomMentionPill[];
  onDeleteMention?: (key: string) => void;
  referenceButtonAriaLabel?: string;
  referenceHint?: string;
};

export const PromptBox: React.FC<Props> = ({
  ref,
  initialValue = "",
  disabled = false,
  submitDisabled = false,
  onChange,
  onSubmit,
  placeholder,
  submitLabel = "Send",
  dataLoc,
  customMentionPills,
  onDeleteMention,
  referenceButtonAriaLabel = "Mention an item",
  referenceHint = "Use @ to mention",
}) => {
  const referenceItems = useReferenceItems();
  const { kinds: referenceKinds } = useReferenceRendering();
  const editorRef = useRef<MDXEditorMethods>(null);
  const mentionsApiRef = useRef<PromptBoxMentionsApi | null>(null);
  const pendingMentionsRef = useRef<PromptBoxMentionInsert[]>([]);
  const [isEmpty, setIsEmpty] = useState(initialValue.trim().length === 0);
  const [inlineMentions, setInlineMentions] = useState<PromptBoxMention[]>([]);

  const appendMention = (mention: PromptBoxMentionInsert) => {
    const editor = editorRef.current;
    const mentionsApi = mentionsApiRef.current;
    if (!editor || !mentionsApi) {
      pendingMentionsRef.current = [...pendingMentionsRef.current, mention];
      return;
    }
    // The insert command needs a selection, so focus the end first. It leaves
    // the cursor right after the inserted mention.
    editor.focus(
      () => mentionsApi.insertMention({ trigger: "@", ...mention }),
      { defaultSelection: "rootEnd" },
    );
    setIsEmpty(false);
  };

  useImperativeHandle(ref, () => ({ appendMention }));

  const handleChange = (value: string) => {
    setIsEmpty(value.trim().length === 0);
    setInlineMentions(mentionsApiRef.current?.getMentions() ?? []);
    onChange?.(value);
  };

  const handleInitialMentions = (mentions: PromptBoxMention[]) => {
    setInlineMentions(mentions);
    const pending = pendingMentionsRef.current;
    pendingMentionsRef.current = [];
    pending.map(appendMention);
  };

  const cannotSubmit = disabled || submitDisabled || isEmpty;

  const handleSubmit = () => {
    if (cannotSubmit) return;
    // Re-check the live content: the isEmpty mirror can be ahead of the
    // lazy-mounted editor.
    const editor = editorRef.current;
    const content = editor?.getMarkdown() ?? "";
    if (!editor || !content.trim()) return;
    onSubmit(content, mentionsApiRef.current?.getMentions() ?? []);
    editor.setMarkdown("");
    setIsEmpty(true);
    setInlineMentions([]);
  };

  const handleOpenMentionMenu = () => {
    editorRef.current?.focus();
    mentionsApiRef.current?.openMentionMenu({ trigger: "@" });
  };

  const handleRemoveInlineMention = (mention: PromptBoxMention) => {
    mentionsApiRef.current?.removeMentions(mentionKey(mention));
    setInlineMentions(mentionsApiRef.current?.getMentions() ?? []);
  };

  const inlinePills = dedupeMentions(inlineMentions);
  const hasPills =
    (customMentionPills?.length ?? 0) > 0 || inlinePills.length > 0;
  const hasMentionItems = referenceItems.length > 0;

  return (
    <div
      className="flex w-full flex-col gap-2 overflow-hidden rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm"
      data-loc={dataLoc}
    >
      {hasPills && (
        <div className="flex flex-wrap gap-1">
          {customMentionPills?.map((pill) => (
            <MentionPill
              {...pill}
              key={pill.key}
              isDeletable={pill.isDeletable && !disabled}
              onDelete={
                pill.isDeletable ? () => onDeleteMention?.(pill.key) : undefined
              }
            />
          ))}
          {inlinePills.map((mention) => (
            <MentionPill
              key={`mention:${mentionKey(mention)}`}
              isDeletable={!disabled}
              label={mention.value}
              icon={resolveKind(referenceKinds, mention.data?.kind)?.icon}
              variant={resolvePillVariant(
                typeof mention.data?.variant === "string"
                  ? mention.data.variant
                  : undefined,
              )}
              onDelete={() => handleRemoveInlineMention(mention)}
            />
          ))}
        </div>
      )}
      <div className="relative">
        <Suspense fallback={<Skeleton height="h-5" rounded="rounded-lg" />}>
          <PromptBoxEditor
            ref={editorRef}
            disabled={disabled}
            initialValue={initialValue}
            mentionItems={referenceItems}
            mentionKinds={referenceKinds}
            placeholder={placeholder}
            mentionsApiRef={mentionsApiRef}
            onChange={handleChange}
            onEnterSubmit={handleSubmit}
            onInitialMentions={handleInitialMentions}
          />
        </Suspense>
      </div>
      <div className="h-px w-full rounded-full bg-gray-100" />
      <div className="flex items-center gap-1.5">
        <div className="flex flex-1 items-center gap-1.5">
          {hasMentionItems && (
            <>
              <IconButton
                aria-label={referenceButtonAriaLabel}
                dataLoc="chat-prompt-mention"
                disabled={disabled}
                icon={faAt}
                size="xs"
                onClick={handleOpenMentionMenu}
              />
              <p className="font-inter-medium-11px text-gray-500">
                {referenceHint}
              </p>
            </>
          )}
        </div>
        <Button
          dataLoc="chat-prompt-submit"
          isDisabled={cannotSubmit}
          htmlType="submit"
          iconRight={faArrowTurnDownLeft}
          size="sm"
          variant="primary"
          onClick={handleSubmit}
        >
          {submitLabel}
        </Button>
      </div>
    </div>
  );
};
